import 'client-only';

import { useMemo } from 'react';

import {
  CHECKOUT_MODES,
  type CheckoutMode,
} from '@/components/flow/steps/checkout/types';
import {
  getCardFullSizeItemPrices,
  getCardTrialSizeItemPrices,
} from '@/helpers/productSelectionAndCheckout/helpers';
import {
  type ItemPrices,
  type Card,
  type GiftWithPurchaseData,
  type PromotionClaim,
} from '@/types/productSelectionAndCheckout/types';

import { useCheckoutMode } from './useCheckoutMode';
import { useProductSelectionAndCheckoutCommonData } from './useProductSelectionAndCheckoutCommonData';
import { usePromotionClaim } from './usePromotionClaim';
import { useSelectedItemIds } from './useSelectedItemIds';
import { useTax } from './useTax';
import { useTrialSizeItemPrices } from './useTrialSizeItemPrices';

// eslint-disable-next-line sonarjs/cognitive-complexity
export function getTotals({
  checkoutMode,
  dermatologyConsultation,
  fullSizeItemPrices,
  handling,
  isTwoMonthFirstShipment,
  promotionClaim,
  selectedCards,
  shipping,
  tax,
  trialSizeItemPrices,
}: {
  checkoutMode: CheckoutMode;
  dermatologyConsultation: number;
  fullSizeItemPrices: ItemPrices;
  giftWithPurchaseData: GiftWithPurchaseData;
  handling: number;
  isTwoMonthFirstShipment?: boolean;
  promotionClaim?: PromotionClaim;
  selectedCards: Card[];
  shipping: number;
  tax: number;
  trialSizeItemPrices: ItemPrices;
}) {
  const sAndH = handling + shipping;
  const dermatologyConsultationAndSAndH = dermatologyConsultation + sAndH;

  let futureShipmentTotal = 0;
  let subscriptionDiscountTotal = 0;
  let trialDiscountTotal = 0;
  let trialDiscountTotalWithDermatologyConsultationAndSAndH = 0;
  let trialTotalNoTax = dermatologyConsultationAndSAndH;
  let subscriptionTotalNoTax = 0;
  let smallAgencySubscriptionTotal = 0;
  let subscriptionTotalWithTax = tax;
  let trialTotalWithoutDiscounts = dermatologyConsultationAndSAndH;
  let trialTotalWithTax = dermatologyConsultationAndSAndH + tax;

  let trialTotalWithTaxAndWithoutDiscounts =
    dermatologyConsultationAndSAndH + tax;

  selectedCards.forEach((card) => {
    const cardFullSizeItemPrices = getCardFullSizeItemPrices(
      card,
      fullSizeItemPrices,
    );

    const cardTrialSizeItemPrices = getCardTrialSizeItemPrices(
      card,
      isTwoMonthFirstShipment,
      trialSizeItemPrices,
    );

    const cardItemPrices =
      checkoutMode === CHECKOUT_MODES.Trial
        ? cardTrialSizeItemPrices
        : cardFullSizeItemPrices;

    futureShipmentTotal += cardFullSizeItemPrices?.finalAmount || 0;
    subscriptionTotalNoTax += cardFullSizeItemPrices?.finalAmount || 0;
    subscriptionTotalWithTax += cardFullSizeItemPrices?.finalAmount || 0;
    trialDiscountTotal += cardItemPrices?.adjustmentAmount || 0;

    if (card.isSmallItemSubscribable) {
      smallAgencySubscriptionTotal += cardItemPrices?.originalAmount || 0;
    } else {
      smallAgencySubscriptionTotal +=
        cardFullSizeItemPrices?.originalAmount || 0;
    }

    subscriptionDiscountTotal += cardFullSizeItemPrices?.adjustmentAmount || 0;

    trialDiscountTotalWithDermatologyConsultationAndSAndH +=
      cardItemPrices?.adjustmentAmount || 0;

    trialTotalNoTax += cardItemPrices?.finalAmount || 0;

    trialTotalWithoutDiscounts += cardItemPrices?.originalAmount || 0;

    trialTotalWithTax += cardItemPrices?.finalAmount || 0;

    trialTotalWithTaxAndWithoutDiscounts += cardItemPrices?.originalAmount || 0;
  });

  if (promotionClaim) {
    trialDiscountTotalWithDermatologyConsultationAndSAndH +=
      promotionClaim.amount;

    trialDiscountTotalWithDermatologyConsultationAndSAndH = Math.max(
      trialDiscountTotalWithDermatologyConsultationAndSAndH,
      0,
    );

    trialTotalNoTax -= promotionClaim.amount;
    trialTotalNoTax = Math.max(trialTotalNoTax, 0);

    trialTotalWithTax -= promotionClaim.amount;
    trialTotalWithTax = Math.max(trialTotalWithTax, 0);
  }

  return {
    futureShipmentTotal,
    smallAgencySubscriptionTotal,
    subscriptionTotalNoTax,
    subscriptionTotalWithTax,
    subscriptionDiscountTotal,
    trialDiscountTotal,
    trialDiscountTotalWithDermatologyConsultationAndSAndH,
    trialTotalNoTax,
    trialTotalWithoutDiscounts,
    trialTotalWithTax,
    trialTotalWithTaxAndWithoutDiscounts,
  };
}

export const useTotals = () => {
  const checkoutMode = useCheckoutMode();
  const {
    dermatologyConsultation,
    flags: { isTwoMonthFirstShipment },
    giftWithPurchaseData,
    handling,
    shipping,
    fullSizeItemPrices,
  } = useProductSelectionAndCheckoutCommonData();
  const { selectedCards } = useSelectedItemIds();
  const trialSizeItemPrices = useTrialSizeItemPrices();
  const { promotionClaim } = usePromotionClaim();
  const { tax } = useTax();

  return useMemo(
    () =>
      getTotals({
        checkoutMode,
        dermatologyConsultation,
        fullSizeItemPrices,
        giftWithPurchaseData,
        handling,
        isTwoMonthFirstShipment,
        shipping,
        tax,
        promotionClaim,
        trialSizeItemPrices,
        selectedCards,
      }),
    [
      checkoutMode,
      dermatologyConsultation,
      fullSizeItemPrices,
      giftWithPurchaseData,
      handling,
      isTwoMonthFirstShipment,
      promotionClaim,
      selectedCards,
      shipping,
      tax,
      trialSizeItemPrices,
    ],
  );
};
